import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, ChevronLeft, ChevronRight, Radio, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { getMetaPages, createBroadcast } from '@/services/api';
import { getPageVariables } from '@/services/api/connections';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';
import type { ContentBlock } from '@/types/flow';
import StepConfigureBlocks from './components/StepConfigureBlocks';
import StepSchedule from './components/StepSchedule';
import StepReview from './components/StepReview';
import MessengerPreview from './components/MessengerPreview';
import type { BroadcastFormData } from './types/broadcast.types';

const STEPS = [
    { number: 1, title: 'Conteúdo', description: 'Monte a mensagem' },
    { number: 2, title: 'Agendar', description: 'Escolha quando enviar' },
    { number: 3, title: 'Revisar', description: 'Confirme os detalhes' },
];

export default function BroadcastEditorPage() {
    const { pageId } = useParams<{ pageId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const numericPageId = Number(pageId);

    // Carrega páginas para encontrar dados da página selecionada
    const { data: allPages = [], isLoading: loadingPages } = useQuery({
        queryKey: ['meta-pages'],
        queryFn: async () => {
            const data = await getMetaPages();
            const seen = new Set<string>();
            return data.filter(p => {
                if (seen.has(p.page_id)) return false;
                seen.add(p.page_id);
                return true;
            });
        },
        staleTime: 1000 * 60 * 5,
    });

    // Encontra a página pelo id numérico do banco
    const page = allPages.find(p => p.id === numericPageId) as MetaPage | undefined;

    // Carrega variáveis da página
    const { data: pageVariables = [] } = useQuery({
        queryKey: ['page-variables', numericPageId],
        queryFn: () => getPageVariables(numericPageId),
        enabled: !isNaN(numericPageId) && numericPageId > 0,
        staleTime: 1000 * 60 * 2,
    });

    const prefillData = location.state?.prefill as BroadcastFormData | undefined;

    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState<BroadcastFormData>(prefillData || {
        selectedPages: [],
        blocks: [],
        isScheduled: false,
        scheduledTime: '',
        // Default = utility (fora da janela 24h) — caso mais comum no uso real.
        broadcastType: 'utility',
        messageTag: 'ACCOUNT_UPDATE',
        aiTranslate: false,
        sourceLanguage: undefined,
        targetLanguage: 'Inglês - Estados Unidos',
        aiProvider: 'openai',
    });

    // Quando a página carrega, garante que selectedPages corresponde à página da URL
    useEffect(() => {
        if (page) {
            setFormData(prev => ({
                ...prev,
                selectedPages: [{
                    id: page.id,
                    page_id: page.page_id,
                    page_name: page.page_name,
                    page_access_token: page.page_access_token
                }],
            }));
        }
    }, [page?.id]);

    const hasBlockContent = (block: ContentBlock) => {
        if (block.type === 'text') return !!block.message?.trim();
        return !!(block.media_url || block.attachment_id);
    };

    const canProceed = (): boolean => {
        switch (currentStep) {
            case 1: return formData.blocks.length > 0 && formData.blocks.some(hasBlockContent);
            case 2: return !formData.isScheduled || !!formData.scheduledTime;
            case 3: return true;
            default: return false;
        }
    };

    const createMutation = useMutation({
        mutationFn: () =>
            createBroadcast({
                title: formData.title || undefined,
                blocks: formData.blocks,
                page_ids: [page!.page_id],
                is_scheduled: formData.isScheduled,
                scheduled_at: formData.isScheduled ? formData.scheduledTime : undefined,
                message_tag: formData.messageTag ?? undefined,
                ai_translate: formData.aiTranslate,
                source_language: formData.sourceLanguage || undefined,
                target_language: formData.targetLanguage,
                ai_provider: formData.aiProvider,
                audience_window: formData.audienceWindow ?? 'all',
            }),
        onSuccess: () => {
            const description = formData.aiTranslate
                ? 'Broadcast criado! A tradução automática está sendo gerada em background.'
                : 'O broadcast foi criado com sucesso.';
            toast({ title: 'Broadcast criado!', description });
            queryClient.invalidateQueries({ queryKey: ['broadcasts'] });
            navigate('/meta/broadcasts');
        },
        onError: (err: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar broadcast',
                description: err.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    if (loadingPages) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
        );
    }

    if (!page && !loadingPages) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <p className="text-gray-500">Página não encontrada.</p>
                <Button variant="outline" onClick={() => navigate('/meta/broadcasts')}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
                </Button>
            </div>
        );
    }

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <StepConfigureBlocks
                        blocks={formData.blocks}
                        onChange={blocks => setFormData(prev => ({ ...prev, blocks }))}
                        pageId={page?.page_id}
                        pageVariables={pageVariables}
                    />
                );
            case 2:
                return (
                    <StepSchedule
                        isScheduled={formData.isScheduled}
                        scheduledTime={formData.scheduledTime}
                        broadcastType={formData.broadcastType || 'standard'}
                        messageTag={formData.messageTag}
                        onIsScheduledChange={value => setFormData({ ...formData, isScheduled: value })}
                        onScheduledTimeChange={value => setFormData({ ...formData, scheduledTime: value })}
                        onBroadcastTypeChange={(type, tag) => setFormData({ ...formData, broadcastType: type, messageTag: tag })}
                    />
                );
            case 3:
                return (
                    <StepReview
                        formData={formData}
                        onChange={(aiTranslate, targetLanguage, aiProvider, sourceLanguage) => setFormData(prev => ({ ...prev, aiTranslate, targetLanguage, aiProvider, sourceLanguage }))}
                        onAudienceWindowChange={(audienceWindow) => setFormData(prev => ({ ...prev, audienceWindow }))}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex flex-col h-full min-h-screen bg-gray-50">
            {/* Top Header */}
            <div className="bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#0f172a] px-6 py-4 flex items-center gap-4">
                <button
                    onClick={() => navigate('/meta/broadcasts')}
                    className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors text-sm"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Broadcasts
                </button>
                <div className="w-px h-5 bg-white/20" />
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-white/10 rounded-lg flex items-center justify-center">
                        <Radio className="w-4 h-4 text-sky-300" />
                    </div>
                    <div>
                        <span className="text-white font-semibold text-sm">Novo Broadcast</span>
                        {page && (
                            <span className="ml-2 text-sky-300 text-xs">— {page.page_name}</span>
                        )}
                    </div>
                </div>

                {/* Step indicator (right side) */}
                <div className="ml-auto flex items-center gap-2">
                    {STEPS.map((step, i) => {
                        const isActive = currentStep === step.number;
                        const isDone = currentStep > step.number;
                        return (
                            <div key={step.number} className="flex items-center gap-2">
                                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${isDone ? 'bg-green-500 text-white' :
                                    isActive ? 'bg-sky-400 text-white' :
                                        'bg-white/10 text-slate-400'
                                    }`}>
                                    {isDone ? <Check className="w-3 h-3" /> : <span>{step.number}</span>}
                                    <span>{step.title}</span>
                                </div>
                                {i < STEPS.length - 1 && (
                                    <div className={`w-8 h-px ${isDone ? 'bg-green-400' : 'bg-white/20'}`} />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Title bar (nome do broadcast) */}
            <div className="bg-white border-b border-gray-200 px-6 py-3">
                <Input
                    placeholder="Nome do broadcast (ex: Campanha Black Friday)"
                    value={formData.title || ''}
                    onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="text-sm"
                />
            </div>

            {/* Main content */}
            <div className="flex flex-1 overflow-hidden">
                {/* Left: step content */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-y-auto p-6">
                        {renderStep()}
                    </div>

                    {/* Footer buttons */}
                    <div className="border-t border-gray-200 bg-white px-6 py-4 flex justify-between">
                        <Button
                            variant="outline"
                            onClick={() => currentStep > 1 ? setCurrentStep(s => s - 1) : navigate('/meta/broadcasts')}
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            {currentStep > 1 ? 'Anterior' : 'Cancelar'}
                        </Button>

                        {currentStep < STEPS.length ? (
                            <Button
                                onClick={() => setCurrentStep(s => s + 1)}
                                disabled={!canProceed()}
                                className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                            >
                                Próximo
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        ) : (
                            <Button
                                onClick={() => createMutation.mutate()}
                                disabled={createMutation.isPending}
                                className="bg-green-600 hover:bg-green-700 text-white"
                            >
                                {createMutation.isPending ? 'Criando...' : 'Confirmar e Criar'}
                                <Check className="w-4 h-4 ml-1" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Right: preview */}
                <div className="w-80 bg-white border-l border-gray-200 flex flex-col">
                    <div className="border-b border-gray-100 px-4 py-3">
                        <h3 className="text-sm font-semibold text-gray-700 text-center">Preview</h3>
                        {page && (
                            <div className="mt-1 flex items-center gap-1.5 justify-center">
                                <div className="w-5 h-5 bg-[#0f172a] rounded-full flex items-center justify-center">
                                    <span className="text-white text-[9px] font-bold">
                                        {page.page_name.substring(0, 2).toUpperCase()}
                                    </span>
                                </div>
                                <span className="text-xs text-gray-500">{page.page_name}</span>
                            </div>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto p-4">
                        <MessengerPreview
                            blocks={formData.blocks}
                            pageName={page?.page_name}
                        />
                    </div>

                    {/* Variables sidebar panel */}
                    {pageVariables.length > 0 && (
                        <div className="border-t border-gray-100 p-3">
                            <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-2">
                                Variáveis da página
                            </p>
                            <div className="flex flex-wrap gap-1">
                                {pageVariables.map(v => (
                                    <div
                                        key={v.id}
                                        className="group relative"
                                        title={v.value}
                                    >
                                        <span className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-[10px] font-mono cursor-default">
                                            {'{{'}{v.key}{'}}'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
